// @ts-check

// If you're adding a new feature, you must add it to
// app/src/scripts/utils/ubpUtils.js so it shows up in the UI
import {
    OPENAI_PRICING,
    getConversionRateForModel,
    priceToCreditConversion
} from "../openAiProxy/openAiPricing";
import { getEmailConversionRate } from "../emailProxy/emailPricing";
import {
    getSmsConversionRate,
    getMmsConversionRate,
    getSmsInternationalConversionRate,
    getMmsInternationalConversionRate
} from "../twilioProxy/twilioPricing";
import { getGoogleVisionOCRConversionRate } from "../googleVisionProxy/googleVisionPricing";
import { GEMINI_PRICING, getConversionRateForGeminiModel } from "../geminiProxy/geminiPricing";
import {
    ANTHROPIC_PRICING,
    getConversionRateForAnthropicModel
} from "../anthropicProxy/anthropicPricing";
import { WEBRTC_PLAN_RATES } from "../webrtc/webrtcService";

export const UBP_BILLING_FEATURE_ROBOFLOW_AGENT_PROVIDER_COST =
    "third-party-api--provider-cost-usd";

export const UBP_METER_ID_AI_LABELING = "ai-labeling";
export const UBP_METER_ID_HOSTED_INFERENCE = "hostedInference";
export const UBP_METER_ID_TRAIN = "train";
export const UBP_METER_ID_IMAGE_SOURCE_STORAGE = "image-source-storage";
export const UBP_METER_ID_IMAGE_TRANSFORM_STORAGE = "image-transform-storage";
export const UBP_METER_ID_IMAGE_GENERATION = "image-generation";
export const UBP_METER_ID_IMAGE_INDEXING = "image-indexing";
export const UBP_METER_ID_VISION_EVENTS_INGESTION = "vision-events-ingestion";
export const UBP_METER_ID_BATCH_VIDEO_INFERENCE = "batch-video-inference";
export const UBP_METER_ID_DEDICATED_MACHINE_HOURLY_DEV_CPU = "dedicated-machine-hourly_dev-cpu";
export const UBP_METER_ID_DEDICATED_MACHINE_HOURLY_DEV_GPU = "dedicated-machine-hourly_dev-gpu";
export const UBP_METER_ID_DEDICATED_MACHINE_HOURLY_PROD_CPU = "dedicated-machine-hourly_prod-cpu";
export const UBP_METER_ID_DEDICATED_MACHINE_HOURLY_PROD_GPU = "dedicated-machine-hourly_prod-gpu";
export const UBP_METER_ID_SELF_HOSTED_STREAM = "self-hosted-stream";
export const UBP_METER_ID_SELF_HOSTED_INFERENCE_RUN = "self-hosted-inference-run";
export const UBP_METER_ID_BATCH_PROCESSING_COMPUTE_GPU = "batch-processing-compute-gpu";
export const UBP_METER_ID_BATCH_PROCESSING_COMPUTE_CPU = "batch-processing-compute-cpu";
export const UBP_METER_ID_SERVERLESS_INFERENCE_RUN = "serverless-inference-run";
export const UBP_METER_ID_DEVICE_LOGS_INGESTED = "device-logs-ingested";

const _BASE_CONVERSION_RATES = {
    // unit: 1 image
    // 1 credit = 100 images
    [UBP_METER_ID_AI_LABELING]: 1 / 100,

    // unit: 1 inference
    // 1 credit = 1000 inferences
    [UBP_METER_ID_HOSTED_INFERENCE]: 1 / 1000,

    // unit: 1 train second
    // 1 credit = 30 minutes
    [UBP_METER_ID_TRAIN]: 1 / 30 / 60,
    //"train_rfdetr": 1 / 30 / 60 / 2,

    // unit: 1 image / percentage of month
    // 1 credit = 5000 monthly images
    [UBP_METER_ID_IMAGE_SOURCE_STORAGE]: 1 / 5000,

    // unit: 1 image / percentage of month
    // 1 credit = 5000 monthly images
    [UBP_METER_ID_IMAGE_TRANSFORM_STORAGE]: 1 / 5000,

    // unit: 1 image
    // 1 credit = 20000 images
    [UBP_METER_ID_IMAGE_GENERATION]: 1 / 20000,

    // unit: 1 image
    // 1 credit = 10000 images indexed
    [UBP_METER_ID_IMAGE_INDEXING]: 1 / 10000,

    // unit: 1 event
    // 1 credit = 10,000 events ingested
    [UBP_METER_ID_VISION_EVENTS_INGESTION]: 1 / 10000,

    // unit: 1 hour x 1 FPS (inference FPS not video FPS)
    // 1 credit = 200 minutes per FPS
    [UBP_METER_ID_BATCH_VIDEO_INFERENCE]: 1 / (200 / 60),

    // unit: 1 hour
    // 1 credit = 4 hour
    [UBP_METER_ID_DEDICATED_MACHINE_HOURLY_DEV_CPU]: 1 / 4,

    // unit: 1 hour
    // 1 credit = 1 hour
    [UBP_METER_ID_DEDICATED_MACHINE_HOURLY_DEV_GPU]: 1,

    // unit: 1 hour
    // 1 credit = 4 hour
    [UBP_METER_ID_DEDICATED_MACHINE_HOURLY_PROD_CPU]: 1 / 4,

    // unit: 1 hour
    // 1 credit = 1 hour
    [UBP_METER_ID_DEDICATED_MACHINE_HOURLY_PROD_GPU]: 1,

    // unit: 1 second
    // 1 credit = 500 minutes
    [UBP_METER_ID_SELF_HOSTED_STREAM]: 1 / 500 / 60,

    // unit: 1 run
    // 1 credit = 3,000 inference runs (workflows or model)
    // equivalent of 48 hours of live video at 1 frame per minute
    [UBP_METER_ID_SELF_HOSTED_INFERENCE_RUN]: 1 / 3000,

    // unit: 1 second
    // 4 credits = 1 GPU / hr
    [UBP_METER_ID_BATCH_PROCESSING_COMPUTE_GPU]: 4 / 3600,

    // unit: 1 second
    // 1 credits = 1 CPU / hr
    [UBP_METER_ID_BATCH_PROCESSING_COMPUTE_CPU]: 1 / 3600,

    // unit: 1 second
    // 1 credits = 500 seconds
    [UBP_METER_ID_SERVERLESS_INFERENCE_RUN]: 1 / 500,

    // unit: 1 log line ingested
    // 1 credit = 1,000,000 log lines
    [UBP_METER_ID_DEVICE_LOGS_INGESTED]: 1 / 1000000,

    // unit: 1 USD provider cost (Roboflow Agent / workflow assistants, gateway-reported $)
    [UBP_BILLING_FEATURE_ROBOFLOW_AGENT_PROVIDER_COST]: priceToCreditConversion(1)
};

/**
 * UBP meter keys that map 1:1 to the same string in usage (excludes the agent provider cost key,
 * which the paywall passes as `featureId` as {@link UBP_BILLING_FEATURE_ROBOFLOW_AGENT_PROVIDER_COST}).
 * Source of truth for this list is {@link _BASE_CONVERSION_RATES} keys.
 */
export const UBP_METER_IDENTITY_SOURCE_KEYS = Object.freeze(
    Object.keys(_BASE_CONVERSION_RATES).filter(
        (k) => k !== UBP_BILLING_FEATURE_ROBOFLOW_AGENT_PROVIDER_COST
    )
);

const _DEFAULT_CONVERSION_RATES = { ..._BASE_CONVERSION_RATES };

for (const model of Object.keys(OPENAI_PRICING)) {
    const inputRate = getConversionRateForModel(model, "input");
    const outputRate = getConversionRateForModel(model, "output");
    if (inputRate) {
        _DEFAULT_CONVERSION_RATES[`third-party-api--open-ai-${model}-input`] = inputRate;
    }
    if (outputRate) {
        _DEFAULT_CONVERSION_RATES[`third-party-api--open-ai-${model}-output`] = outputRate;
    }
}

// Email notification pricing
_DEFAULT_CONVERSION_RATES["third-party-api--email-notification"] = getEmailConversionRate();

// Twilio SMS/MMS notification pricing
// Base rates (US/Canada - used as default fallback)
_DEFAULT_CONVERSION_RATES["third-party-api--twilio-sms"] = getSmsConversionRate();
_DEFAULT_CONVERSION_RATES["third-party-api--twilio-mms"] = getMmsConversionRate();
// Country-parameterized rates - US/Canada (30 messages per credit)
_DEFAULT_CONVERSION_RATES["third-party-api--twilio-sms_us-ca"] = getSmsConversionRate();
_DEFAULT_CONVERSION_RATES["third-party-api--twilio-mms_us-ca"] = getMmsConversionRate();
// Country-parameterized rates - International (10 messages per credit)
_DEFAULT_CONVERSION_RATES["third-party-api--twilio-sms_intl"] = getSmsInternationalConversionRate();
_DEFAULT_CONVERSION_RATES["third-party-api--twilio-mms_intl"] = getMmsInternationalConversionRate();

// Google Vision OCR pricing
_DEFAULT_CONVERSION_RATES["third-party-api--google-vision-ocr"] =
    getGoogleVisionOCRConversionRate();

// WebRTC plan pricing (rates are credits per hour, convert to credits per second)
for (const [plan, creditsPerHour] of Object.entries(WEBRTC_PLAN_RATES)) {
    _DEFAULT_CONVERSION_RATES[plan] = creditsPerHour / 3600;
}

// Gemini API pricing (token-based, like OpenAI)
for (const model of Object.keys(GEMINI_PRICING)) {
    const inputRate = getConversionRateForGeminiModel(model, "input");
    const outputRate = getConversionRateForGeminiModel(model, "output");
    if (inputRate) {
        _DEFAULT_CONVERSION_RATES[`third-party-api--gemini-${model}-input`] = inputRate;
    }
    if (outputRate) {
        _DEFAULT_CONVERSION_RATES[`third-party-api--gemini-${model}-output`] = outputRate;
    }
}

// Anthropic API pricing (token-based, like OpenAI)
for (const model of Object.keys(ANTHROPIC_PRICING)) {
    const inputRate = getConversionRateForAnthropicModel(model, "input");
    const outputRate = getConversionRateForAnthropicModel(model, "output");
    if (inputRate) {
        _DEFAULT_CONVERSION_RATES[`third-party-api--anthropic-${model}-input`] = inputRate;
    }
    if (outputRate) {
        _DEFAULT_CONVERSION_RATES[`third-party-api--anthropic-${model}-output`] = outputRate;
    }
}

/**
 * Returns all possible feature combinations from a feature string, splitting on underscores
 * @param {string} feature - Feature string with parts separated by underscores
 * @returns {string[]} Array of feature combinations, from most specific to base
 * @example
 * // Returns ['dedicated-machine-hourly_prod-gpu_us-1', 'dedicated-machine-hourly_prod-gpu', 'dedicated-machine-hourly']
 * getFeatureComponents('dedicated-machine-hourly_prod-gpu_us-1')
 */
function getFeatureComponents(feature) {
    const parts = feature.split("_");
    if (parts.length === 1) {
        return [feature];
    }

    const results = [feature];
    while (parts.length > 1) {
        parts.pop();
        results.push(parts.join("_"));
    }
    return results;
}

function featureConversionRate({ usage, feature }) {
    const featureComponents = getFeatureComponents(feature);

    for (const featureComponent of featureComponents) {
        if (
            usage?.creditConversionRate?.[featureComponent] &&
            typeof usage?.creditConversionRate?.[featureComponent] === "number"
        ) {
            return usage?.creditConversionRate?.[featureComponent];
        }

        if (_DEFAULT_CONVERSION_RATES[featureComponent]) {
            return _DEFAULT_CONVERSION_RATES[featureComponent];
        }
    }

    throw new Error(`Unknown feature ${feature} not in conversion rates`);
}

const MAX_CREDITS_PER_ADJUSTMENT = 1000000;

export {
    featureConversionRate,
    _DEFAULT_CONVERSION_RATES,
    getFeatureComponents,
    MAX_CREDITS_PER_ADJUSTMENT
};
export default {
    featureConversionRate,
    _DEFAULT_CONVERSION_RATES,
    getFeatureComponents,
    MAX_CREDITS_PER_ADJUSTMENT
};
